/* globals jQuery, NKABOM */
/* eslint-disable @wordpress/no-global-active-element */

/**
 * External dependencies
 */
import CliboardJS from 'clipboard';

/**
 * Internal dependencies
 */
import delegateEventListener from './theme/delegate-event';
import determineTarget from './theme/determine-target';
import toggleElement from './theme/toggle-element';
import setupStickyObserver from './theme/sticky-observer';
import setupTabs from './theme/tabs';
import setupAccordions from './theme/accordions';
import setupModals from './theme/modals';
import setupDropdowns from './theme/dropdown';
import setupShareButtons from './theme/share-button';
import setupDirectoryModules from './theme/ajax-directory';
import setupHeadingObserver from './theme/heading-observer';

jQuery( $ => {
	// =========================
	// ! Flag Interaction Method
	// =========================

	function flagMouseUse() {
		document.body.classList.add( 'using-mouse' );
		document.body.classList.remove( 'using-keyboard' );
	}
	document.addEventListener( 'mousemove', flagMouseUse );
	document.addEventListener( 'touchstart', flagMouseUse );
	document.addEventListener( 'keydown', event => {
		if ( event.key === 'Tab' ) {
			document.body.classList.remove( 'using-mouse' );
			document.body.classList.add( 'using-keyboard' );
		}
	} );

	// =========================
	// ! External Link Mandate
	// =========================

	document.querySelectorAll( 'a[href^="http"]' ).forEach( anchor => {
		if ( ! anchor.target && ! anchor.href.startsWith( window.location.origin ) ) {
			anchor.setAttribute( 'target', '_blank' );
			anchor.setAttribute( 'rel', 'noreferrer noopener' );
		}
	} );

	// =========================
	// ! Basic Toggles
	// =========================

	delegateEventListener( 'click', '.js-toggle', ( event, trigger ) => {
		const target = determineTarget( trigger );
		if ( target ) {
			event.preventDefault();
			const isOpen = toggleElement( target, null, 'focus' );

			// For global styling that might be needed
			document.body.classList.toggle( `show-${ target.id }`, isOpen );
		}
	} );

	delegateEventListener( 'click', '.js-modal-close', ( event, trigger ) => {
		event.preventDefault();
		const target = trigger.closest( '[aria-hidden="false"]' );
		if ( target ) {
			trigger.blur();
			toggleElement( target, false );
		}
	} );

	document.addEventListener( 'click', event => {
		document.querySelectorAll( '.js-click-outside-to-close' ).forEach( element => {
			if ( ! element.contains( event.target ) ) {
				toggleElement( element.querySelector( '[aria-hidden="false"]' ), false );
			}
		} );
	} );

	// =========================
	// ! Intersection Observers
	// =========================

	if ( typeof IntersectionObserver !== 'undefined' ) {
		setupStickyObserver();

		setupHeadingObserver( 'h3.wp-block-heading[id]', '.nkabom-article' );
	}

	// =========================
	// ! Peek-a-boo Nav Handling
	// =========================

	let lastScroll = 0;
	let lastDirection = null;
	let lastPastHeader = null;
	const headerThreshold = () => 36;

	window.addEventListener( 'scroll', () => {
		const scroll = window.scrollY;
		const direction = scroll >= lastScroll ? 1 : -1;
		const pastHeader = scroll > headerThreshold();

		if ( direction !== lastDirection || pastHeader !== lastPastHeader ) {
			document.body.classList.toggle( 'animating-sticky-nav--in', direction < 0 && pastHeader );
			document.body.classList.toggle( 'animating-sticky-nav--out', direction > 0 && pastHeader );
		}

		lastScroll = scroll;
		lastDirection = direction;
		lastPastHeader = pastHeader;
	} );

	// =========================
	// ! Modals
	// =========================

	// Add classes to modals automatically
	$( '.nkabom-modal' ).addClass( 'js-modal' ).appendTo( document.body );
	$( '.nkabom-modal__close' ).addClass( 'js-modal-close' );
	$( '.nkabom-modal__frame' ).addClass( 'js-modal-backdrop' );

	setupModals( NKABOM.endpoints || [] );

	// =========================
	// ! Tab Set
	// =========================

	// Add classes to tab/tabset blocks automatically
	$( '.nkabom-tab-set' ).addClass( 'js-tab-set js-accordion-group' );
	$( '.nkabom-tab-set__nav-item' ).addClass( 'js-tab' );
	$( '.nkabom-tab-panel' ).addClass( 'js-tab-panel' );
	$( '.nkabom-tab-header__toggle' ).addClass( 'js-accordion-toggle js-mobile-toggle' );
	$( '.nkabom-tab-panel__content' ).addClass( 'js-accordion-content js-mobile-accordion' );

	// Mark first tab as auto-open accordion
	$( '.nkabom-tab-set' ).each( function() {
		$( this ).find( '.nkabom-tab-panel__toggle' ).first().addClass( 'auto-open' );
	} );

	setupTabs();

	// =========================
	// ! Accordion
	// =========================

	// Add classes to accordion blocks automatically
	$( '.nkabom-accordion__toggle' ).addClass( 'js-accordion-toggle' );
	$( '.nkabom-accordion__content' ).addClass( 'js-accordion-content' );

	// Auto open conditions: contains search highlight, or is target
	$( '.nkabom-accordion:has(mark) .nkabom-accordion__toggle' ).addClass( 'auto-open' );
	if ( document.location.hash.length > 1 ) {
		$( `.nkabom-accordion${ document.location.hash } .nkabom-accordion__toggle` ).addClass( 'auto-open' );
	}

	setupAccordions( NKABOM.endpoints || [] );

	// =========================
	// ! Scroller Buttons
	// =========================

	function makeScrollerButton( type ) {
		const button = document.createElement( 'div' );
		button.className = `nkabom-scroller__button ${ type }`;
		button.setAttribute( 'aria-hidden', true );
		return button;
	}

	const scrollerGaps = new WeakMap();
	const scrollerGapObserver = new ResizeObserver( entries => {
		entries.forEach( entry => {
			const gap = parseInt( getComputedStyle( entry.target ).gap.replace( 'px', '' ) );
			scrollerGaps.set( entry.target, gap );
		} );
	} );

	document.querySelectorAll( '.nkabom-scroller' ).forEach( scroller => {
		const header = scroller.querySelector( '.nkabom-scroller__header' );
		const inner = scroller.querySelector( '.nkabom-scroller__inner' );

		let nav = scroller.querySelector( '.nkabom-scroller__nav' );
		if ( ! nav ) {
			nav = document.createElement( 'div' );
			nav.classList.add( 'nkabom-scroller__nav' );
			header.append( nav );
		}

		const nextButton = makeScrollerButton( 'next' );
		const prevButton = makeScrollerButton( 'prev' );
		const buttons = [ nextButton, prevButton ];

		nav.append( prevButton, nextButton );

		prevButton.hidden = true;
		inner.addEventListener( 'scroll', () => {
			nextButton.hidden = inner.scrollLeft + inner.offsetWidth >= inner.scrollWidth;
			prevButton.hidden = inner.scrollLeft === 0;
		} );

		scrollerGapObserver.observe( inner );

		const sampleItem = inner.firstElementChild;
		buttons.forEach( button => {
			button.addEventListener( 'click', () => {
				if ( button.hidden ) {
					return;
				}

				const gap = scrollerGaps.get( inner ) ?? 0;
				if ( button.classList.contains( 'next' ) ) {
					inner.scrollBy( {
						left: sampleItem.offsetWidth + gap,
					} );
				} else {
					inner.scrollBy( {
						left: -1 * ( sampleItem.offsetWidth + gap ),
					} );
				}
			} );
		} );
	} );

	document.addEventListener( 'keyup', event => {
		if ( event.key === 'Tab' ) {
			const scroller = document.activeElement?.closest( '.nkabom-scroller__inner' );
			if ( scroller ) {
				document.activeElement.scrollIntoView( {
					block: 'nearest',
					inline: 'nearest',
				} );
			}
		}
	} );

	// =========================
	// ! Filterable Listing
	// =========================

	document.querySelectorAll( '.nkabom-listing' ).forEach( listing => {
		const filters = listing.querySelectorAll( '[data-filter-value]' );
		const filterables = listing.querySelectorAll( '[data-filterable-values]' );

		const itemFilters = new WeakMap();
		filterables.forEach( item => {
			itemFilters.set( item, item.dataset.filterableValues?.split( ',' ) ?? [] );
		} );

		function filterItems( value ) {
			filters.forEach( button => button.setAttribute( 'aria-selected', button.dataset.filterValue === value ) );
			filterables.forEach( item => {
				let hidden = true;
				if ( value === 'ALL' || itemFilters.get( item )?.includes( value ) ) {
					hidden = false;
				}

				item.setAttribute( 'aria-hidden', hidden );
			} );

			listing.querySelector( '.nkabom-scroller__inner' )?.scrollTo( 0, 0 );
		}

		listing.addEventListener( 'click', event => {
			const trigger = event.target.closest( '[data-filter-value]' );
			filterItems( trigger.dataset.filterValue );
		} );

		filterItems( 'ALL' );
	} );

	// =========================
	// ! Dropdown
	// =========================

	$( '.gfield:has(.ginput_container_select)' ).addClass( 'js-dropdown' );

	setupDropdowns();

	// =========================
	// ! Excerpt Handling
	// =========================

	$( document ).on( 'click', '.js-expand', function() {
		const expanded = $( this ).closest( '.is-expandable' ).toggleClass( 'is-expanded' ).hasClass( 'is-expanded' );

		// Swap the text with the cached label
		const label = $( this ).data( 'label' ) || ( expanded ? 'Show less' : 'Show more' );
		$( this ).data( 'label', $( this ).text() );
		$( this ).text( label );
	} );

	// =========================
	// ! Hidden Target Handling
	// =========================

	function handleHiddenTarget( event ) {
		// Get the target element
		const target = document.getElementById( window.location.hash.substring( 1 ) );
		if ( ! target ) {
			return;
		}

		// See if it's within a hidden container
		const container = target.closest( '[id][aria-hidden="true"]' );
		if ( ! container ) {
			return;
		}

		// Find the control for the container
		const control = document.querySelector( `[aria-controls="${ container.id }"], [data-target="${ container.id }"]` );
		if ( ! control ) {
			return;
		}

		// Prevent automatic scolling
		if ( event ) {
			event.preventDefault();
		}

		// Open the container and focus
		control.click();
		target.scrollIntoView();
	}

	window.addEventListener( 'hashchange', handleHiddenTarget );
	handleHiddenTarget();

	// =========================
	// ! Bookmark/Share Links
	// =========================

	setupShareButtons();

	$( 'a.js-clipboard' ).on( 'click', e => e.preventDefault() );

	const hrefClipboard = new CliboardJS( 'a.js-clipboard', {
		text( trigger ) {
			return trigger.getAttribute( 'href' );
		},
	} );

	const clipboardTooltipTimeouts = new WeakMap();
	hrefClipboard.on( 'success', e => {
		clearTimeout( clipboardTooltipTimeouts.get( e.trigger ) );
		$( e.trigger ).addClass( 'js-copied' ).attr( 'aria-label', 'Link copied!' );
		const timeout = setTimeout( () => $( e.trigger ).removeClass( 'js-copied' ), 5000 );
		clipboardTooltipTimeouts.set( e.trigger, timeout );
	} );

	// =========================
	// ! Directory Module
	// =========================

	setupDirectoryModules();

	// =========================
	// ! Audio Controls
	// =========================

	document.querySelectorAll( '.wp-block-audio' ).forEach( block => {
		const audio = block.querySelector( 'audio' );
		const play = block.querySelector( '.js-play' );
		const stop = block.querySelector( '.js-stop' );

		if ( ! play || ! stop ) {
			return;
		}

		audio.addEventListener( 'play', () => {
			audio.classList.add( 'is-playing' );
			play.setAttribute( 'aria-hidden', true );
			stop.setAttribute( 'aria-hidden', false );
		} );

		function stopped() {
			audio.classList.remove( 'is-playing' );
			play.setAttribute( 'aria-hidden', false );
			stop.setAttribute( 'aria-hidden', true );
		}

		audio.addEventListener( 'pause', stopped );
		audio.addEventListener( 'ended', stopped );
		audio.addEventListener( 'abort', stopped );

		play.addEventListener( 'click', () => audio.play() );
		stop.addEventListener( 'click', () => audio.stop() );
	} );

	// =========================
	// ! Map Modules
	// =========================

	document.querySelectorAll( '.js-map' ).forEach( map => {
		const canvas = map.querySelector( '.js-map-canvas' );
		const filters = map.querySelectorAll( '.js-map-filter' );

		const $tooltip = $( '<div class="js-map-tooltip" aria-hidden="true"></div>' ).appendTo( canvas );

		$( canvas )
			.on( 'mouseenter', () => $tooltip.addClass( 'in' ) )
			.on( 'mouseleave', () => $tooltip.removeClass( 'in' ) )
			.on( 'mouseenter mousemove', ( { originalEvent: { target, clientX, clientY } } ) => {
				const $target = $( target ).closest( '.js-map-region, .js-map-marker' );

				$tooltip
					.text( $target.data( 'title' ) || null )
					.css( {
						top: clientY + 'px',
						left: clientX + 'px',
					} );
			} );

		const itemsWrap = map.querySelector( '.js-map-items' );
		const items = [ ...map.querySelectorAll( '.js-map-item' ) ];

		const perPage = parseInt( map.dataset.perpage ?? 10 );

		const pagination = map.querySelector( '.js-map-pagination' );
		const current = map.querySelector( '.js-map-pagination-current' );
		const total = map.querySelector( '.js-map-pagination-total' );
		const prevPage = map.querySelector( '.js-map-pagination-prev' );
		const nextPage = map.querySelector( '.js-map-pagination-next' );

		let currentPage = 0;
		let totalPages = 0;

		function updatePagination( page ) {
			currentPage = page;

			items.filter( item => ! item.classList.contains( 'excluded' ) )
				.forEach( ( item, index ) => {
					item.hidden = Math.floor( index / perPage ) !== currentPage;
				} );

			current.innerText = currentPage + 1;
			total.innerText = totalPages;

			prevPage.disabled = currentPage <= 0;
			nextPage.disabled = currentPage >= totalPages - 1;
		}

		prevPage.addEventListener( 'click', () => updatePagination( currentPage - 1 ) );
		nextPage.addEventListener( 'click', () => updatePagination( currentPage + 1 ) );

		function recalculatePagination() {
			const visible = items.filter( item => ! item.classList.contains( 'excluded' ) ).length;

			currentPage = 0;
			totalPages = Math.ceil( visible / perPage );

			pagination.hidden = visible <= perPage;

			updatePagination( 0 );
		}

		recalculatePagination();

		let ignoreFilters = false;
		function filterItems() {
			items.forEach( item => {
				let isExcluded = false;

				filters.forEach( filter => {
					if ( filter.value && ! $( item ).hasClass( `${ filter.name }-${ filter.value }` ) ) {
						isExcluded = true;
					}
				} );

				item.classList.toggle( 'excluded', isExcluded );
			} );

			recalculatePagination();
		}

		function maybeFilterItems() {
			if ( ! ignoreFilters ) {
				filterItems();
			}
		}

		filters.forEach( filter => filter.addEventListener( 'change', maybeFilterItems ) );

		const regionFilter = map.querySelector( '[name="region"]' );
		regionFilter?.addEventListener( 'change', () => {
			$( map ).find( '[data-region]' ).removeClass( 'is-focused' );
			$( map ).find( `[data-region="${ regionFilter.value }"]` ).addClass( 'is-focused' );
		} );
		$( map ).on( 'click', '.js-map-marker, .js-map-region.has-items', function() {
			const region = $( this ).data( 'region' );
			if ( region ) {
				regionFilter.value = region;
				regionFilter.dispatchEvent( new Event( 'change' ) );
			}
		} );

		map.querySelector( 'button[type="reset"]' ).addEventListener( 'click', () => {
			ignoreFilters = true;
			filters.forEach( filter => {
				filter.value = '';
				filter.dispatchEvent( new Event( 'change' ) );
			} );
			ignoreFilters = false;
			filterItems();
		} );
	} );
} );
